/*
*   File : portfolio-rebalance.js
*   Author : https://evoqins.com
*   Description : Portfolio rebalance page
*   Integrations : NA
*   Version : 1.0.0
*/

import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal } from 'bootstrap';


//************ Components  **************// 
import { PrimaryButton, TextButton } from '../../Component/Buttons';
import { Chip, Loader } from '../../Component/Others';
import { _checkBrokerStatus, _createAndUpdatePortFolio, _executeBrokerOrder, _formattedAmount, _generateProductCode, _getMarketStatus, _openBrokerOrderPlatform, _scrollToTop, _validateBroker, _validateBrokerOrder, BROKERS_LIST_DATA } from '../../Helper';
import { APIService } from '../../Service';
import { RebalanceStatusModal } from '../../Component/Modal';
import { Icon } from '../../Component/Icon';

//************ Styles  **************// 
import style from "../../Styles/Component/card.module.scss";


const PortfolioRebalance = () => {

    const USER_DATA = useSelector((store) => store.Reducer.USER_DATA);
    const navigate = useNavigate();
    const { state } = useLocation();
    const orderStatusData = useRef();

    let broker_data = JSON.parse(localStorage.getItem("indvestment_connected_broker"));
    let product_data = state && state.portfolio_data || {};
    let basket_data = state && state.basket_data || {};
    let portfolio_holdings = product_data.strategy_id ? product_data.current_holdings : [];

    const [rebalanceHoldings, setRebalanceHoldings] = useState([]);
    const [holdingsSellList, setHoldingsSellList] = useState([]);
    const [holdingsBuyList, setHoldingsBuyList] = useState([]);
    const [currentStep, setCurrentStep] = useState(1);
    const [loader, setLoader] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);

    useEffect(() => {
        // API call
        _initiateRebalance();
    }, []);

    useEffect(() => {
        if (showStatusModal === true) {
            const rebalance_modal = new Modal(document.getElementById('rebalance-status-modal'));
            rebalance_modal.show();
        }
    }, [showStatusModal]);

    // handle back
    const _handleBack = () => {
        if (currentStep === 2) {
            setCurrentStep(1);
        } else {
            navigate(-1);
        }
    };

    // handle navigate to portfolio 
    const _handleNavigateToPortfolio = () => {
        navigate("/portfolio");
    };

    // handle navigate to creator detail page
    const _handleNavigateToCreatorDetail = () => {
        if (product_data.creator_id) {
            navigate("/explore/creator-detail", {
                state: {
                    creator_id: Number(product_data.creator_id)
                }
            });
        }
    };

    // handle show redeem and rebalance 
    const _handleTriggerRebalance = () => {
        setLoader(true);
        _callGetMarketStatus();
    };

    // handle - move to rebalance and view details
    const _handleModalSuccess = () => {
        setShowStatusModal(false);
        setCurrentStep(2);
    };



    // ===================================== //
    // ============ API CALLS ============== //

    // handle call get Product code
    const _callGenerateProductCode = async () => {

        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: rebalanceHoldings,
            // action: action,
            portfolio_id: product_data.portfolio_id,
            user_id: USER_DATA.unique_identifier,
            operation: "REBALANCING"
        };

        const response = await _generateProductCode(params);

        if (response.status_code === 200) {
            if (broker_data.publisher) {
                await _callOpenBrokerOrderPlatform(response.data.product_code, response.data.portfolio_action_id);
            } else {
                // Call execute API - Manual
                await _callExecuteBrokerOrder(response.data.product_code, response.data.portfolio_action_id);
            }
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoader(false);
        }

    };

    // handle call open broker order platform
    const _callOpenBrokerOrderPlatform = async (product_code, portfolio_action_id) => {

        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: rebalanceHoldings,
            // transaction_type: action,
            portfolio_id: product_data.portfolio_id,
            product_code: product_code,
            user_id: USER_DATA.unique_identifier,
        };

        const response = await _openBrokerOrderPlatform(params);

        if (response.status_code === 200) {
            // Open broker order window
            const broker_window_url = URL.createObjectURL(
                new Blob([response.result], { type: "text/html" })
            );
            // Broker order creation window
            const broker_order_window = window.open(broker_window_url, "_blank");
            let current_url = "";
            let interval = setInterval(() => {
                try {
                    current_url = broker_order_window.location.href;
                    let url_data = current_url.split('?');
                    if (url_data.length !== 1) {
                        // Taking the type and action from the URL
                        let params = new URLSearchParams(url_data[1]);
                        let access_token = params.get("request_token");
                        const type = params.get("type");
                        const action = params.get("action");
                        const status = params.get("status");
                        if (type === "basket" && action === "basket" && access_token && status === "success") {
                            clearInterval(interval);
                            // API call
                            _callValidateBrokerOrder(access_token, product_code, portfolio_action_id);
                            broker_order_window.close();
                        } else if (status === "cancelled") {
                            clearInterval(interval);
                            broker_order_window.close();
                            toast.dismiss();
                            toast.error("Rebalance has been cancelled");
                            setLoader(false);
                        }
                    }
                } catch (error) {
                    console.log("Cross-origin access error. Waiting for the window to navigate...");
                }

                if (broker_order_window.closed) {
                    clearInterval(interval);
                    if (current_url.length === 0) {
                        setLoader(false);
                    }
                };

            }, 1000);

        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoader(false);
        }

    };

    // handle call execute Broker Order 
    const _callExecuteBrokerOrder = async (product_code, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            holdings: rebalanceHoldings,
            product_code: product_code,
            user_id: USER_DATA.unique_identifier,
        };

        const response = await _executeBrokerOrder(params);
        if (response.status_code === 200) {
            // API 
            _callUpdatePortFolio(product_code, portfolio_action_id);
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoader(false);
        };

    };

    // handle call validate broker order
    const _callValidateBrokerOrder = async (access_token, product_code, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            user_id: USER_DATA.unique_identifier,
            creator_id: basket_data.creator_info.creator_id,
            access_token: access_token,
        };

        const response = await _validateBrokerOrder(params);

        if (response.status_code === 200) {
            await _callUpdatePortFolio(product_code, portfolio_action_id);
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoader(false);
        }
    };

    // handle call update portfolio
    const _callUpdatePortFolio = async (product_code, portfolio_action_id) => {
        let params = {
            basket_data: basket_data,
            broker_data: broker_data,
            user_id: USER_DATA.unique_identifier,
            product_code: product_code,
            portfolio_id: product_data.portfolio_id,
            investment_amount: product_data.investment_value,
            operation: "REBALANCING",
            portfolio_action_id: portfolio_action_id
        };

        const response = await _createAndUpdatePortFolio(params);

        if (response.status_code === 200 || response.status_code === 202 || response.status_code === 204) {
            // Redemption status  = success, partial, awaiting confirmation 
            setShowStatusModal(true);
            orderStatusData.current = {
                type: 1,
                status_message: response.message,
            };
        } else if (response.status_code === 205) {
            // Order rejected
            setShowStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
            };
        } else if (response.status_code === 400) {
            setShowStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
            };
        } else {
            // Something went wrong
            setShowStatusModal(true);
            orderStatusData.current = {
                type: 2,
                status_message: response.message,
                status_code: response.status_code
            };
        }
        setLoader(false);
    };

    // Call get Market status
    const _callGetMarketStatus = async () => {

        const response = await _getMarketStatus();

        if (response.status_code === 200) {
            if (response.data.market_status === "Open") {
                _callGenerateProductCode();
            } else {
                toast.dismiss();
                toast.error("Market closed, you can't rebalance today");
                setLoader(false);
            }
        } else {
            toast.dismiss();
            toast.error(response.message);
            setLoader(false);
        }
    };

    // start Rebalance
    const _initiateRebalance = async () => {

        let date_now = new Date();
        let url = "broker/rebalance";
        let data = {
            "remaining_amt": 0,
            "strategy_type": null,
            "strategy_id": product_data.strategy_id,
            "date": `${date_now.getFullYear()}-${date_now.getMonth() + 1}-${date_now.getDate()}`,
            "portfolio_details": portfolio_holdings.map((holding) => ({
                ...holding,
                "qty": holding.filled_quantity,
                "invested_price": holding.invested_amount
            })),
            "user_id": USER_DATA.unique_identifier,
            "action": "Rebalancer",
            "request_date": date_now,
        };


        let response = await APIService(url, data);
        if (response.status_code === 200) {

            let holdings = response.data.portfolio_details.map(holding => {
                return {
                    ...holding,
                    "quantity": holding.action_qty,
                    "nse_exchange_symbol": holding.symbol,
                    "bse_exchange_symbol": holding.symbol
                }
            }).filter((item) => item.action !== "HOLD");

            setRebalanceHoldings(holdings);




            let redeem_list = holdings.filter(item => item.action === "SELL");
            if (redeem_list.length !== 0) {
                redeem_list = redeem_list.map(item => ({
                    ...item,
                    amount: item.action_qty * item.close
                }));
            }

            let rebalance_list = holdings.filter(item => item.action === "BUY");
            if (rebalance_list.length !== 0) {
                rebalance_list = rebalance_list.map(item => ({
                    ...item,
                    amount: item.action_qty * item.close
                }));
            }

            setHoldingsSellList(redeem_list);
            setHoldingsBuyList(rebalance_list);

        } else {
            navigate("/portfolio");
            toast.dismiss();
            toast.error(response.message);
        }

        setDataLoading(false);
    };


    //\\\\\\\\\\\\\\\\\\\\ Inline components \\\\\\\\\\\\\\\\\\\\\\
    const ProductCard = () => {
        return (
            <div className={`${style.e_alphabasket_card_wrapper} cursor-pointer mt-4`}>
                <div className='e-bg-white border-radius-16 h-100'>
                    <div data-type={product_data.strategy_type === "alpha-basket" ? "basket" : "basket-pro"}
                        className={`${style.e_heading_wrapper} position-relative`}>
                        <img src={require(product_data.strategy_type === "alpha-basket" ? "../../Assets/Images/Card/alphabasket-tag.svg" : "../../Assets/Images/Card/alphabasket-pro-tag.svg").default}
                            draggable={false}
                            className='position-absolute left--6px top-8px h-28'
                            alt='tag' />
                        <Chip label={product_data.risk_category_info.risk_category_name}
                            className={product_data.risk_category_info.risk_category_name === 'Conservative' ? 'e-text-deep-green e-bg-nyanza e-border-1px-light-green' :
                                product_data.risk_category_info.risk_category_name === 'Moderate' ? 'e-text-brown e-bg-lemon-chiffon e-border-1px-jasmine' :
                                    'e-text-cadmium-purple e-bg-lavender-blush e-border-1px-light-red'} />
                    </div>
                    <div className='p-3'>
                        <div className='d-flex align-items-center gap-12px e-border-bottom-1px-anti-flash-white pb-3'>
                            <img src={product_data.strategy_icon}
                                draggable={false}
                                className='border-radius-4 bg-white p-1'
                                width={48}
                                alt={product_data.strategy_name} />
                            <div>
                                <p className='e-font-14-res mb-1 e-text-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px'>
                                    {product_data.strategy_name}
                                </p>
                                {
                                    product_data.creator_name &&
                                    <span className='e-font-12-res mb-0 e-text-philippine-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-18px text-decoration-hover-underline'
                                        onClick={_handleNavigateToCreatorDetail}>
                                        by {product_data.creator_name || "--"}
                                    </span>
                                }
                            </div>
                        </div>

                        <div className='pt-2'>
                            <div className='row justify-content-between'>
                                <div className='col-5'>
                                    <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                        Rebalance issued
                                    </p>
                                    <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                        {product_data.last_rebalancing_date || "--"}
                                    </p>
                                </div>
                                <div className='col-3'>
                                    <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                        No. of Stocks
                                    </p>
                                    <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                        {rebalanceHoldings.length}
                                    </p>
                                </div>
                                <div className='col-4 text-end'>
                                    <p className='mb-1 e-text-outer-space e-alt-font-lato e-font-12 e-font-weight-400 line-height-18px'>
                                        Total Investment
                                    </p>
                                    <p className={`e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-20px`}>
                                        {_formattedAmount(product_data.investment_value)}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    };

    return (
        <>
            {
                dataLoading ?
                    <Loader wrapperClassName="min-vh-100 margin-200px-top" />
                    :
                    <div className="min-h-100vh pb-5">

                        {/* Rebalancing details */}
                        {
                            currentStep === 1 &&
                            <div>
                                <TextButton name="Rebalancing"
                                    type={2}
                                    icon={true}
                                    onPress={_handleBack} />
                                <div className="row mt-4">
                                    {
                                        (holdingsBuyList.length === 0 && holdingsSellList.length === 0) ?
                                            <div className="col col-sm-9 col-md-7">
                                                <div className="ms-2 me-lg-5 d-flex gap-2 py-4 padding-12px-lr e-bg-magnolia e-border-0-5px-pale-lavender border-radius-8">
                                                    <Icon icon={"note-icon"}
                                                        className="d-flex w-18px" />
                                                    <p className="mb-0 e-text-chinese-black e-alt-font-lato e-font-weight-400 e-font-14 line-height-18px">
                                                        No action required. Your portfolio is already aligned with the model portfolio.
                                                    </p>
                                                </div>
                                            </div>
                                            :
                                            <div className="col col-sm-9 col-md-7">

                                                <h3 className='e-font-14-res e-primary-font-clash-display e-text-chinese-black e-font-weight-500 e-font-16 line-height-24px mb-1'>
                                                    Rebalance Objective
                                                </h3>
                                                <p className='e-alt-font-lato e-text-outer-space e-font-weight-400 e-font-14 line-height-20px mb-0'>
                                                    {/* Lorem ipsum dolor sit amet consectetur. Id pellentesque elit imperdiet sed neque feugiat semper iaculis scelerisque. Vitae eget aliquam nisi scelerisque. Tincidunt orci fusce purus elit ipsum a tortor fames. */}
                                                </p>

                                                {/* Product info card */}
                                                <ProductCard />

                                                {/* Stock Breakdown */}
                                                <div className="mt-4 pt-2">
                                                    <h3 className='e-font-14-res mb-2 e-primary-font-clash-display e-text-chinese-black e-font-16 e-font-weight-500 line-height-24px'>
                                                        Basket Constituents
                                                    </h3>

                                                    {/* Stocks to Buy */}
                                                    {
                                                        holdingsBuyList.length !== 0 &&
                                                        <div className="">
                                                            <h4 className='e-font-14-res e-alt-font-lato e-text-primary-color e-font-weight-600 e-font-16 line-height-24px mb-3'>
                                                                Stocks to Buy
                                                            </h4>
                                                            <div className='e-border-1px-anti-flash-white border-radius-8'>
                                                                <div className='e-bg-magnolia px-3 padding-10px-tb'>
                                                                    <div className='row flex-nowrap justify-content-between'>
                                                                        <div className='col-8'>
                                                                            <h6 className='mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px'>
                                                                                {holdingsBuyList.length === 1 ? "Stock" : "Stocks"} ({holdingsBuyList.length})
                                                                            </h6>
                                                                        </div>
                                                                        <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                            <h6 className='text-nowrap text-sm-wrap mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px cursor-pointer'>
                                                                                Amount
                                                                            </h6>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Added stocks */}
                                                                <div className='px-3'>
                                                                    {
                                                                        holdingsBuyList.map((item, index) => (
                                                                            <div className='row flex-nowrap e-border-bottom-1px-anti-flash-white py-2 align-items-center justify-content-between'
                                                                                key={index}>
                                                                                <div className='col-8'>
                                                                                    <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                                        {item.script_name}
                                                                                    </p>
                                                                                    <p className='mb-1 e-text-slate-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                                        {item.symbol}
                                                                                    </p>

                                                                                    <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-12 e-font-weight-400 line-height-20px'>
                                                                                        Quantity: <span className='e-text-primary-color e-font-weight-600'>{item.action_qty}</span>
                                                                                    </p>
                                                                                </div>

                                                                                <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                                    <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                                        {_formattedAmount(item.amount)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>

                                                                {/* Total Amount */}
                                                                <div className='px-3 padding-18px-tb'>
                                                                    <div className='row flex-nowrap justify-content-between'>
                                                                        <div className='col-8'>
                                                                            <h6 className='e-font-14-res  mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                                Total Amount
                                                                            </h6>
                                                                        </div>
                                                                        <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                            <h6 className='e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-24px'>
                                                                                {_formattedAmount(holdingsBuyList.reduce((initial, b) => initial + Number(b.amount), 0))}
                                                                            </h6>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    }

                                                    {/* Redeem list */}
                                                    {
                                                        holdingsSellList.length !== 0 &&
                                                        <div className="mt-3">
                                                            <h4 className='e-font-14-res e-alt-font-lato e-text-primary-color e-font-weight-600 e-font-16 line-height-24px mb-3'>
                                                                Stocks to Redeem
                                                            </h4>
                                                            <div className='e-border-1px-anti-flash-white border-radius-8'>
                                                                <div className='e-bg-magnolia px-3 padding-10px-tb'>
                                                                    <div className='row flex-nowrap justify-content-between'>
                                                                        <div className='col-8'>
                                                                            <h6 className='mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px'>
                                                                                {holdingsSellList.length === 1 ? "Stock" : "Stocks"} ({holdingsSellList.length})
                                                                            </h6>
                                                                        </div>
                                                                        <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                            <h6 className='text-nowrap text-sm-wrap mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px cursor-pointer'>
                                                                                Amount
                                                                            </h6>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Added stocks */}
                                                                <div className='px-3'>
                                                                    {
                                                                        holdingsSellList.map((item, index) => (
                                                                            <div className='row flex-nowrap e-border-bottom-1px-anti-flash-white py-2 align-items-center justify-content-between'
                                                                                key={index}>
                                                                                <div className='col-8'>
                                                                                    <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                                        {item.script_name}
                                                                                    </p>
                                                                                    <p className='mb-1 e-text-slate-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                                        {item.symbol}
                                                                                    </p>

                                                                                    <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-12 e-font-weight-400 line-height-20px'>
                                                                                        Quantity: <span className='e-text-primary-color e-font-weight-600'>{item.action_qty}</span>
                                                                                    </p>
                                                                                </div>

                                                                                <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                                    <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                                        {_formattedAmount(item.amount)}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        ))
                                                                    }
                                                                </div>

                                                                {/* Total Amount */}
                                                                <div className='px-3 padding-18px-tb'>
                                                                    <div className='row flex-nowrap justify-content-between'>
                                                                        <div className='col-8'>
                                                                            <h6 className='e-font-14-res  mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                                Total Amount
                                                                            </h6>
                                                                        </div>
                                                                        <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                            <h6 className='e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-24px'>
                                                                                {_formattedAmount(holdingsSellList.reduce((initial, b) => initial + Number(b.amount), 0))}
                                                                            </h6>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    }
                                                </div>

                                                {/* Benefits of Rebalancing */}
                                                <div className="mt-4">
                                                    <h3 className='e-font-14-res e-primary-font-clash-display e-text-chinese-black e-font-weight-500 e-font-16 line-height-24px mb-2'>
                                                        Benefits of Rebalancing
                                                    </h3>
                                                    <ul className='ps-3'>
                                                        <li className='e-alt-font-lato e-text-chinese-black e-font-weight-600 e-font-14 line-height-20px mb-1'>
                                                            Risk Management
                                                        </li>
                                                        <p className='e-alt-font-lato e-text-outer-space e-font-weight-400 e-font-14 line-height-18px'>
                                                            Maintain your preferred level of risk by adjusting asset weights.
                                                        </p>
                                                        <li className='e-alt-font-lato e-text-chinese-black e-font-weight-600 e-font-14 line-height-20px mb-1'>
                                                            Return Optimization
                                                        </li>
                                                        <p className='e-alt-font-lato e-text-outer-space e-font-weight-400 e-font-14 line-height-18px'>
                                                            Capture gains and reinvest in under performing assets.
                                                        </p>
                                                        <li className='e-alt-font-lato e-text-chinese-black e-font-weight-600 e-font-14 line-height-20px mb-1'>
                                                            Discipline
                                                        </li>
                                                        <p className='e-alt-font-lato e-text-outer-space e-font-weight-400 e-font-14 line-height-18px'>
                                                            Stay committed to your investment Methodology and avoid emotional decision-making.
                                                        </p>
                                                    </ul>
                                                </div>

                                                <div className="mt-5 d-flex gap-2 py-2 padding-12px-lr e-bg-magnolia e-border-0-5px-pale-lavender border-radius-8">
                                                    <Icon icon={"note-icon"}
                                                        className="d-flex w-18px" />
                                                    <p className="mb-0 e-text-chinese-black e-alt-font-lato e-font-weight-400 e-font-14 line-height-18px">
                                                        If required by your broker, please authenticate T-PIN before placing an order
                                                    </p>
                                                </div>

                                                <PrimaryButton
                                                    name="Rebalance"
                                                    status={loader}
                                                    disabled={loader}
                                                    padding="padding-18px-tb e-padding-8px-tb-res"
                                                    fontSize="e-font-14-res e-font-16"
                                                    // className="h-54 mt-3 col-9"
                                                    className="h-54 mt-4 col-9"
                                                    onPress={_handleTriggerRebalance} />
                                            </div>
                                    }
                                </div>
                            </div>
                        }

                        {/* Rebalanced details - stocks bought and redeemed */}
                        {
                            currentStep === 2 &&
                            <div className="card-box-shadow e-bg-white p-3 border-radius-16 min-h-100vh">
                                <TextButton name="Rebalance Details"
                                    type={2}
                                    icon={false} />

                                <div className="row flex-column-reverse flex-lg-row">
                                    {/* Stock Breakdown */}
                                    <div className="col-sm-10 col-lg-7">
                                        <div className="mt-4 pt-2">
                                            <h3 className='e-font-14-res  mb-2 e-primary-font-clash-display e-text-chinese-black e-font-16 e-font-weight-500 line-height-24px'>
                                                Stock Breakdown
                                            </h3>

                                            {/* Stocks Bought */}
                                            <div>
                                                <h4 className='e-font-14-res e-alt-font-lato e-text-primary-color e-font-weight-600 e-font-16 line-height-24px mb-3'>
                                                    Stocks Bought
                                                </h4>
                                                <div className='e-border-1px-anti-flash-white border-radius-8'>
                                                    <div className='e-bg-magnolia px-3 padding-10px-tb'>
                                                        <div className='row flex-nowrap justify-content-between'>
                                                            <div className='col-8'>
                                                                <h6 className='mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px'>
                                                                    {holdingsBuyList.length === 1 ? "Stock" : "Stocks"} ({holdingsBuyList.length})
                                                                </h6>
                                                            </div>
                                                            <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                <h6 className='text-nowrap text-sm-wrap mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px cursor-pointer'>
                                                                    Amount
                                                                </h6>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Added stocks */}
                                                    <div className='px-3'>
                                                        {
                                                            holdingsBuyList.map((item, index) => (
                                                                <div className='row flex-nowrap e-border-bottom-1px-anti-flash-white py-2 align-items-center justify-content-between'
                                                                    key={index}>
                                                                    <div className='col-8'>
                                                                        <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                            {item.script_name}
                                                                        </p>
                                                                        <p className='mb-1 e-text-slate-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                            {item.symbol}
                                                                        </p>

                                                                        <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-12 e-font-weight-400 line-height-20px'>
                                                                            Quantity: <span className='e-text-primary-color e-font-weight-600'>{item.action_qty}</span>
                                                                        </p>
                                                                    </div>

                                                                    <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                        <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                            {_formattedAmount(item.amount)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>

                                                    {/* Total Amount */}
                                                    <div className='px-3 padding-18px-tb'>
                                                        <div className='row flex-nowrap justify-content-between'>
                                                            <div className='col-8'>
                                                                <h6 className='e-font-14-res  mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                    Total Amount
                                                                </h6>
                                                            </div>
                                                            <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                <h6 className='e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-24px'>
                                                                    {_formattedAmount(holdingsBuyList.reduce((initial, b) => initial + Number(b.amount), 0))}
                                                                </h6>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Stocks Redeemed */}
                                            <div className="mt-4">
                                                <h4 className='e-font-14-res e-alt-font-lato e-text-primary-color e-font-weight-600 e-font-16 line-height-24px mb-3'>
                                                    Stocks Redeemed
                                                </h4>
                                                <div className='e-border-1px-anti-flash-white border-radius-8'>
                                                    <div className='e-bg-magnolia px-3 padding-10px-tb'>
                                                        <div className='row flex-nowrap justify-content-between'>
                                                            <div className='col-8'>
                                                                <h6 className='mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px'>
                                                                    {holdingsSellList.length === 1 ? "Stock" : "Stocks"} ({holdingsSellList.length})
                                                                </h6>
                                                            </div>
                                                            <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                <h6 className='text-nowrap text-sm-wrap mb-0 e-text-black e-alt-font-lato e-font-12 e-font-weight-600 line-height-20px cursor-pointer'>
                                                                    Amount
                                                                </h6>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    {/* Added stocks */}
                                                    <div className='px-3'>
                                                        {
                                                            holdingsSellList.map((item, index) => (
                                                                <div className='row flex-nowrap e-border-bottom-1px-anti-flash-white py-2 align-items-center justify-content-between'
                                                                    key={index}>
                                                                    <div className='col-8'>
                                                                        <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                            {item.script_name}
                                                                        </p>
                                                                        <p className='mb-1 e-text-slate-gray e-alt-font-lato e-font-14 e-font-weight-400 line-height-20px'>
                                                                            {item.symbol}
                                                                        </p>

                                                                        <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-12 e-font-weight-400 line-height-20px'>
                                                                            Quantity: <span className='e-text-primary-color e-font-weight-600'>{item.action_qty}</span>
                                                                        </p>
                                                                    </div>

                                                                    <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                        <p className='mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                            {_formattedAmount(item.amount)}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            ))
                                                        }
                                                    </div>

                                                    {/* Total Amount */}
                                                    <div className='px-3 padding-18px-tb'>
                                                        <div className='row flex-nowrap justify-content-between'>
                                                            <div className='col-8'>
                                                                <h6 className='e-font-14-res  mb-0 e-text-chinese-black e-alt-font-lato e-font-14 e-font-weight-600 line-height-20px'>
                                                                    Total Amount
                                                                </h6>
                                                            </div>
                                                            <div className='col-4 col-sm-3 text-sm-end text-center'>
                                                                <h6 className='e-font-14-res mb-0 e-text-chinese-black e-alt-font-lato e-font-16 e-font-weight-600 line-height-24px'>
                                                                    {_formattedAmount(holdingsSellList.reduce((initial, b) => initial + Number(b.amount), 0))}
                                                                </h6>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <PrimaryButton name="View Portfolio"
                                                padding="padding-18px-tb e-padding-8px-tb-res"
                                                fontSize="e-font-14-res e-font-16"
                                                className="h-54 mt-4 col-9"
                                                onPress={_handleNavigateToPortfolio} />
                                        </div>
                                    </div>

                                    <div className="col-sm-10 col-lg-5 pt-lg-4">
                                        {/* Product info card */}
                                        <ProductCard />
                                    </div>
                                </div>
                            </div>
                        }


                        {/* Status modal */}
                        {
                            showStatusModal &&
                            <RebalanceStatusModal type={orderStatusData.current.type}
                                statusMessage={orderStatusData.current.status_message}
                                handleClose={() => setShowStatusModal(false)}
                                handleSuccess={_handleModalSuccess} />
                        }
                    </div>
            }
        </>

    );
};

export default PortfolioRebalance;